import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { login, verifyAccessCode, forgotPassword, resetPassword } from '../services/api';

const LOGO = `${process.env.PUBLIC_URL || ''}/logo.png`;
const SUBTITLE = 'Sign in to your account';

// Step 2 — partial-PIN entry.
function AccessCodeStep({ positions, onSubmit, onBack, loading, error }) {
  const [digits, setDigits] = useState(() => positions.map(() => ''));
  const refs = useRef([]);

  useEffect(() => { refs.current[0]?.focus(); }, []);

  const setAt = (i, v) => {
    const next = [...digits];
    next[i] = v;
    setDigits(next);
  };

  const handleChange = (i, raw) => {
    const d = (raw.match(/\d/g) || []).join('').slice(-1);
    setAt(i, d);
    if (d && i < positions.length - 1) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && i > 0) {
      refs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i < positions.length - 1) {
      refs.current[i + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData.getData('text').match(/\d/g) || []).slice(0, positions.length);
    if (pasted.length) {
      e.preventDefault();
      const next = positions.map((_, i) => pasted[i] || '');
      setDigits(next);
      const last = Math.min(pasted.length, positions.length) - 1;
      refs.current[Math.max(last, 0)]?.focus();
    }
  };

  const code = digits.join('');
  const complete = code.length === positions.length;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (complete) onSubmit(code);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <p className="mb-1 text-sm font-medium text-ink-200">Access code</p>
        <p className="text-xs text-ink-400">
          Enter the digits at the positions below from your registered mobile number.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-red-300">
          <AlertCircle size={18} />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <div className="flex justify-between gap-2" onPaste={handlePaste}>
        {positions.map((p, i) => (
          <div key={p} className="flex flex-col items-center gap-1">
            <span className="text-[11px] font-medium text-ink-400">Position {p}</span>
            <input
              ref={(el) => (refs.current[i] = el)}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              maxLength={1}
              value={digits[i]}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className="h-12 w-12 rounded-xl border border-white/10 bg-ink-800 text-center text-lg font-bold text-white focus:border-brand-500 focus:outline-none"
            />
          </div>
        ))}
      </div>

      <button type="submit" disabled={!complete || loading} className="btn-primary w-full">
        {loading ? 'Verifying…' : 'Verify & sign in'}
      </button>

      <button
        type="button"
        onClick={onBack}
        className="flex w-full items-center justify-center gap-1 text-xs text-ink-400 hover:text-ink-200"
      >
        <ArrowLeft size={12} /> Back to sign in
      </button>
    </form>
  );
}

// Forgot password — enter email, receive link
function ForgotPasswordStep({ onBack }) {
  const [email, setEmail]     = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email.trim().toLowerCase());
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-5">
        <div className="text-center">
          <h2 className="text-lg font-bold text-white">Check your inbox</h2>
          <p className="mt-1 text-sm text-ink-400">
            If <strong className="text-ink-200">{email}</strong> is registered, we've sent a reset link.
            It expires in 1 hour.
          </p>
        </div>
        <button onClick={onBack} className="btn-primary w-full">Back to sign in</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-white">Forgot password?</h2>
        <p className="mt-1 text-xs text-ink-400">Enter your email and we'll send you a reset link.</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-red-300">
          <AlertCircle size={18} />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-ink-300">Email</span>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input"
          placeholder="you@company.com"
          required
          autoFocus
        />
      </label>

      <button type="submit" disabled={loading || !email.trim()} className="btn-primary w-full">
        {loading ? 'Sending…' : 'Send reset link'}
      </button>

      <button
        type="button"
        onClick={onBack}
        className="flex w-full items-center justify-center gap-1 text-xs text-ink-400 hover:text-ink-200"
      >
        <ArrowLeft size={12} /> Back to sign in
      </button>
    </form>
  );
}

// Reset password — set new password using token from URL
function ResetPasswordStep({ token, onDone }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [success, setSuccess]   = useState(false);
  const [error, setError]       = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setError('');
    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      if (!err.response) {
        setError('Your password may have been reset — try signing in with your new password. If it didn\'t work, request a new reset link.');
      } else {
        setError(err.response?.data?.detail || 'Reset failed. The link may have expired.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="space-y-5 text-center">
        <div>
          <h2 className="text-lg font-bold text-white">Password reset!</h2>
          <p className="mt-1 text-sm text-ink-400">Your password has been updated. You can now sign in.</p>
        </div>
        <button onClick={onDone} className="btn-primary w-full">Sign in</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-white">Set new password</h2>
        <p className="mt-1 text-xs text-ink-400">Choose a strong password for your account.</p>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-red-300">
          <AlertCircle size={18} />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-ink-300">New password</span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="input"
          placeholder="Min 8 characters"
          autoComplete="new-password"
          required
          autoFocus
        />
      </label>

      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-ink-300">Confirm password</span>
        <input
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="input"
          placeholder="Repeat password"
          autoComplete="new-password"
          required
        />
      </label>

      <button type="submit" disabled={loading || !password || !confirm} className="btn-primary w-full">
        {loading ? 'Saving…' : 'Set new password'}
      </button>
    </form>
  );
}

export default function Login() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [challenge, setChallenge] = useState(null);
  const [positions, setPositions] = useState([]);
  const [codeError, setCodeError] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);

  const [step, setStep] = useState('credentials'); // credentials | forgot | reset
  const [resetToken, setResetToken] = useState('');

  const navigate = useNavigate();

  // On mount, check URL for reset token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) {
      setResetToken(token);
      setStep('reset');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const finishLogin = (response) => {
    localStorage.setItem('admin_token', response.access_token);
    navigate('/dashboard');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await login(username, password);
      if (response?.requires_access_code) {
        setChallenge(response.challenge);
        setPositions(response.positions || []);
        setCodeError('');
      } else {
        finishLogin(response);
      }
    } catch (err) {
      if (!err.response) {
        setError('Cannot reach the server. Check your internet connection or try again later.');
      } else if (err.response.status === 401) {
        setError('Invalid email or password.');
      } else {
        setError(err.response?.data?.detail || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (code) => {
    setCodeError('');
    setCodeLoading(true);
    try {
      const response = await verifyAccessCode(challenge, code);
      finishLogin(response);
    } catch (err) {
      if (!err.response) {
        setCodeError('Cannot reach the server. Try again in a moment.');
      } else {
        setCodeError(err.response?.data?.detail || 'Verification failed. Please try again.');
      }
    } finally {
      setCodeLoading(false);
    }
  };

  const handleBack = () => {
    setChallenge(null);
    setPositions([]);
    setCodeError('');
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-ink-950 flex items-center justify-center px-4">
      {/* ambient glows */}
      <div className="pointer-events-none absolute -top-24 left-1/2 h-72 w-[40rem] -translate-x-1/2 rounded-full bg-brand-500/15 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-64 w-64 rounded-full bg-brand-600/10 blur-3xl" />

      <div className="card relative z-10 w-full max-w-md p-8">
        <div className="mb-8 text-center">
          <img src={LOGO} alt="Rupadi International" className="mx-auto mb-4 h-16 w-auto" />
          <h1 className="text-2xl font-extrabold text-white">AIETA First</h1>
          <p className="mt-1 text-sm text-ink-400">{SUBTITLE}</p>
        </div>

        {/* ── Reset password (from email link) ── */}
        {step === 'reset' && (
          <ResetPasswordStep token={resetToken} onDone={() => setStep('credentials')} />
        )}

        {/* ── Forgot password ── */}
        {step === 'forgot' && (
          <ForgotPasswordStep onBack={() => setStep('credentials')} />
        )}

        {/* ── Normal sign-in flow ── */}
        {step === 'credentials' && (
          <>
            {challenge ? (
              <AccessCodeStep
                positions={positions}
                onSubmit={handleVerify}
                onBack={handleBack}
                loading={codeLoading}
                error={codeError}
              />
            ) : (
              <>
                {error && (
                  <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-red-300">
                    <AlertCircle size={18} />
                    <span className="text-sm">{error}</span>
                  </div>
                )}

                <form onSubmit={handleLogin} className="space-y-4">
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-ink-300">Email</span>
                    <input
                      type="email"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="input"
                      placeholder="you@company.com"
                      required
                    />
                  </label>

                  <label className="block">
                    <div className="mb-1.5 flex items-center justify-between">
                      <span className="text-sm font-medium text-ink-300">Password</span>
                      <button
                        type="button"
                        onClick={() => setStep('forgot')}
                        className="text-xs text-ink-400 hover:text-ink-200"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input"
                      placeholder="••••••••"
                      required
                    />
                  </label>

                  <button type="submit" disabled={loading} className="btn-primary w-full">
                    {loading ? 'Signing in…' : 'Sign in'}
                  </button>
                </form>
              </>
            )}
          </>
        )}

        <p className="mt-6 text-center text-xs text-ink-500">
          AIETA First · by Rupadi International
        </p>
      </div>
    </div>
  );
}
